<template>
    <span class="inline-flex items-center" @mouseenter="open" @mouseleave="close">
        <Button type="button" text rounded size="small" icon="pi pi-info-circle"
            class="!p-0 !w-6 !h-6 text-gray-500! hover:text-gray-700" :aria-label="`Info ${field}`" />
        <Popover ref="popover">
            <div class="text-sm w-72">
                <div class="font-semibold text-gray-800 mb-2">
                    {{ fieldLabel }}
                </div>

                <div v-if="!rule" class="text-gray-600">
                    Nu există praguri definite pentru acest câmp.
                </div>

                <template v-else>
                    <div class="text-gray-700 mb-2">
                        Sex: <b>{{ sex }}</b>
                    </div>

                    <div class="text-gray-700 mb-3">
                        Valoare curentă:
                        <b>{{ displayValue }}</b>
                        <span v-if="classification" class="ml-2">
                            — încadrat: <b>{{ classification }}</b>
                        </span>
                    </div>

                    <div class="border-t pt-2">
                        <div class="text-xs text-gray-500 mb-2">Praguri ({{ sex }}):</div>

                        <ul class="space-y-1">
                            <li v-for="t in thresholds" :key="t.label" class="flex items-center justify-between">
                                <span class="text-gray-700">{{ t.label }}</span>
                                <span class="font-mono text-gray-700">{{ t.value }}</span>
                            </li>
                        </ul>

                        <div class="mt-2 text-xs text-gray-500">
                            * Pragurile sunt interpretate ca “până la valoarea X”.
                        </div>
                    </div>
                </template>
            </div>
        </Popover>
    </span>
</template>

<script setup>
import { computed, ref, onBeforeUnmount } from "vue";
import Button from "primevue/button";
import Popover from "primevue/popover";
import { inputValoriDeVerificat } from "../../../../src/constante.js";

const props = defineProps({
    field: { type: String, required: true },
    value: { type: [String, Number], default: "" },
    sex: { type: String, default: "F" },
});

const popover = ref(null);
const hideTimer = ref(null);

const open = (event) => {
    if (hideTimer.value) {
        clearTimeout(hideTimer.value);
        hideTimer.value = null;
    }
    popover.value?.show(event);
};

const close = () => {
    if (hideTimer.value) clearTimeout(hideTimer.value);

    hideTimer.value = setTimeout(() => {
        popover.value?.hide();
        hideTimer.value = null;
    }, 120);
};

const fieldLabel = computed(() => props.field);

const rule = computed(() => inputValoriDeVerificat?.[props.field] ?? null);

const classRules = computed(() => {
    if (!rule.value) return null;
    return props.sex === "M" ? rule.value.claseM : rule.value.claseF;
});

const thresholds = computed(() => {
    if (!classRules.value) return [];
    return Object.entries(classRules.value)
        .map(([k, label]) => ({ value: Number(k), label }))
        .sort((a, b) => a.value - b.value);
});

const numericValue = computed(() => {
    const v = typeof props.value === "number" ? props.value : parseFloat(String(props.value).replace(",", "."));
    return Number.isFinite(v) ? v : null;
});

const classification = computed(() => {
    if (!classRules.value || numericValue.value === null) return null;

    const ordered = thresholds.value;
    for (const t of ordered) {
        if (numericValue.value <= t.value) return t.label;
    }
    return ordered.length ? ordered[ordered.length - 1].label : null;
});

const displayValue = computed(() => {
    if (numericValue.value === null) return props.value || "(gol)";
    return numericValue.value;
});

onBeforeUnmount(() => {
    if (hideTimer.value) clearTimeout(hideTimer.value);
});
</script>
